import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Download, Calendar, ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Heading, Text } from '@/components/ui/typography'
import { formatReportingDateInput } from '@/utils/reporting-formatters'

export enum ExportType {
  AGGREGATED = 'aggregated',
  INDIVIDUAL = 'individual',
}

export interface ReportingHeaderProps {
  dateRange: string
  setDateRange: (value: string) => void
  customStartDate: string
  setCustomStartDate: (value: string) => void
  customEndDate: string
  setCustomEndDate: (value: string) => void
  appliedCustomDates: { start: string; end: string } | null
  handleCustomDateRangeApply: () => void
  handleCustomDateRangeCancel: () => void
  handleExport: (type: ExportType) => void
  activeTab: string
}

export function ReportingHeader({
  dateRange,
  setDateRange,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  appliedCustomDates,
  handleCustomDateRangeApply,
  handleCustomDateRangeCancel,
  handleExport,
  activeTab,
}: ReportingHeaderProps) {
  const [customDateRangeOpen, setCustomDateRangeOpen] = useState(false)

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0 animate-in fade-in duration-500 w-full">
      <div className="flex flex-col gap-1">
        <Heading variant="subsection-title">Dashboard</Heading>
      </div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
        <Select
          value={dateRange === 'custom' ? 'custom' : dateRange}
          onValueChange={value => {
            if (value !== 'custom') {
              setDateRange(value)
              setCustomStartDate('')
              setCustomEndDate('')
            }
          }}
        >
          <SelectTrigger className="w-full sm:min-w-[240px] sm:max-w-[300px] h-9 transition-all hover:border-gray-400 dark:hover:border-border/60 border-gray-200 dark:border-border">
            <SelectValue placeholder="Select range" className="truncate" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            {appliedCustomDates && (
              <SelectItem value="custom" className="whitespace-nowrap">
                {`${formatReportingDateInput(appliedCustomDates.start, {
                  month: 'short',
                  day: 'numeric',
                })} - ${formatReportingDateInput(appliedCustomDates.end, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}`}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <Popover
          open={customDateRangeOpen}
          onOpenChange={setCustomDateRangeOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-3 border-gray-200 dark:border-border"
            >
              <Calendar className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 sm:w-80" align="end" sideOffset={8}>
            <div className="space-y-4">
              <div className="space-y-1">
                <Text variant="body" weight="medium">
                  Custom Date Range
                </Text>
                <Text variant="muted">Select start and end dates</Text>
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="start-date" className="text-xs font-medium">
                    Start Date
                  </Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={customStartDate}
                    onChange={e => setCustomStartDate(e.target.value)}
                    max={customEndDate || undefined}
                    className="h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date" className="text-xs font-medium">
                    End Date
                  </Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={customEndDate}
                    onChange={e => setCustomEndDate(e.target.value)}
                    min={customStartDate || undefined}
                    max={new Date().toISOString().split('T')[0]}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleCustomDateRangeCancel()
                    setCustomDateRangeOpen(false)
                  }}
                  className="h-8"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    handleCustomDateRangeApply()
                    setCustomDateRangeOpen(false)
                  }}
                  disabled={!customStartDate || !customEndDate}
                  className="h-8"
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-full sm:w-auto border-gray-200 dark:border-border"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              onClick={() => handleExport(ExportType.AGGREGATED)}
            >
              Aggregated summary
            </DropdownMenuItem>
            {activeTab === 'calls' && (
              <DropdownMenuItem
                onClick={() => handleExport(ExportType.INDIVIDUAL)}
              >
                Individual call metrics
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
